import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import CardMC from '../../../../../../../components/CardMC';
import IconMC from '../../../../../../../components/IconMC';
import { useTools } from '../../../hooks/useTools';
import { editorHtmlToTokenPlainText } from '../../utils/instructionDescriptionTokens';
import DescriptionWithToolPills from '../../components/DescriptionWithToolPills';

const DEFAULT_PREVIEW_MAX_LENGTH = 50;

/** Truncates stored condition without breaking `{{tool:index}}` tokens at the cut. */
const truncateConditionForPreview = (full, maxLength) => {
  if (!full || full.length <= maxLength) {
    return { previewText: full, isTruncated: false };
  }

  let cut = full.slice(0, maxLength);
  const lastOpen = cut.lastIndexOf('{{');
  const lastClose = cut.lastIndexOf('}}');
  if (lastOpen > lastClose) {
    cut = cut.slice(0, lastOpen);
  }

  return { previewText: `${cut}...`, isTruncated: true };
};

const GlobalInstructionSavedCard = ({
  instructionNumber,
  instruction,
  onEdit,
  onDelete,
  showActions = true,
  maxConditionPreviewLength = DEFAULT_PREVIEW_MAX_LENGTH,
  variant = 'default',
  onToolPillClick
}) => {
  const { t } = useTranslation('ChatbotIA');
  const { getToolDetails } = useTools();

  const instructionText = useMemo(
    () => editorHtmlToTokenPlainText(instruction.description || ''),
    [instruction.description]
  );

  const previewDescription = useMemo(() => {
    if (maxConditionPreviewLength == null) return instruction.description || '';
    const { previewText } = truncateConditionForPreview(instructionText, maxConditionPreviewLength);
    return previewText;
  }, [instruction.description, instructionText, maxConditionPreviewLength]);

  const body = (
    <>
      <span className="tw-sr-only">{instructionNumber}</span>
      <div
        className={
          variant === 'modalList'
            ? 'tw-text-sm tw-text-gray-600 tw-whitespace-pre-wrap tw-leading-relaxed'
            : 'tw-text-gray-800 tw-whitespace-pre-wrap tw-leading-relaxed'
        }
      >
        <DescriptionWithToolPills
          description={previewDescription}
          tools={instruction.tools || []}
          getToolDetails={getToolDetails}
          t={t}
          onToolPillClick={onToolPillClick}
          partKeyPrefix={`${instruction.id || instructionNumber}-preview`}
        />
      </div>
    </>
  );

  if (variant === 'modalList') {
    return (
      <div className="tw-min-w-0 tw-flex-1">
        {body}
      </div>
    );
  }

  return (
    <CardMC className="tw-px-3 tw-py-1.5 tw-gap-1 tw-w-full tw-flex tw-justify-between">
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[2px]">
        {body}
      </div>
      {showActions && (
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1">
          <div
            className="tw-cursor-pointer tw-text-gray-400 hover:tw-text-gray-600"
            onClick={onEdit}
          >
            <IconMC name="edit-outline" size="xl" />
          </div>
          <div
            className="tw-cursor-pointer tw-text-gray-400 hover:tw-text-red-500"
            onClick={onDelete}
          >
            <IconMC name="trash-2-outline" size="xl" />
          </div>
        </div>
      )}
    </CardMC>
  );
};

export default GlobalInstructionSavedCard;
